<script>
import { format as formatNumber } from '@/utils/number';
import { hexToRGBA } from '@/utils/color-utils';

export default {
  name: 'OverviewChartSeriesSelector',
  props: {
    series: {
      type: Array,
      default: () => [],
    },
    minSelect: {
      type: Number,
      default: 0,
    },
    maxSelect: {
      type: Number,
      default: 1,
    },
    colors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    // initialize seriesColor for each series
    const seriesColorInitialValue = this.series.reduce(
      (colorsMap, series, index) => {
        const colorValue = series.visible ? this.colors[index] : '';
        colorsMap[index] = colorValue;
        return colorsMap;
      },
      {},
    );

    const selectedSeries = this.series.filter(series => series.visible).length;

    return {
      colorPool: this.colors.slice(selectedSeries.length).reverse(),
      seriesColor: seriesColorInitialValue,
    };
  },
  computed: {
    selectedSeriesIndexes() {
      return this.series.reduce((selectedSeries, series, index) => {
        if (series.visible) selectedSeries.push(index);
        return selectedSeries;
      }, []);
    },
  },
  methods: {
    roundAndFormatNumber(number) {
      return formatNumber(Math.round(number));
    },
    onSeriesItemClick(index) {
      if (this.isSelected(index)) {
        this.deselectSeries(index);
      } else {
        this.selectSeries(index);
      }
    },
    selectSeries(index) {
      if (this.selectedSeriesIndexes.length >= this.maxSelect) {
        return false;
      }

      const color = this.colorPool.pop();
      this.seriesColor[index] = color;

      const updatedSeriesList = this.series.slice();
      updatedSeriesList[index] = {
        ...updatedSeriesList[index],
        color,
        visible: true,
      };

      this.$emit('change', updatedSeriesList);
    },
    deselectSeries(index) {
      if (this.selectedSeriesIndexes.length <= this.minSelect) {
        return false;
      }

      this.colorPool.push(this.seriesColor[index]);

      // reset color pool to original order
      if (this.colorPool.length === this.colors.length) {
        this.colorPool = this.colors.slice().reverse();
      }

      const updatedSeriesList = this.series.slice();
      updatedSeriesList[index] = {
        ...updatedSeriesList[index],
        color: undefined,
        visible: false,
      };

      this.$emit('change', updatedSeriesList);
    },
    getSeriesItemColor(index) {
      if (!this.isSelected(index)) return '';
      return this.seriesColor[index];
    },
    canSelect(index) {
      return (
        this.isSelected(index) ||
        this.selectedSeriesIndexes.length < this.maxSelect
      );
    },
    isSelected(index) {
      return this.selectedSeriesIndexes.includes(index);
    },
    lightenColor(color) {
      return hexToRGBA(color, 0.1);
    },
  },
};
</script>

<template>
  <div class="overview-chart-series-selector">
    <div
      v-for="(sr, i) in series"
      :key="sr.name"
      class="overview-chart-series-selector__item"
    >
      <ElPopover
        placement="top"
        trigger="click"
        popper-class="overview-chart-series-selector__popover"
        :close-delay="0"
        :disabled="canSelect(i)"
        :content="$t('ADVERTISEMENT.OVERVIEW.SERIES_SELECTION_LIMIT')"
      >
        <div
          slot="reference"
          class="overview-chart-series-selector__item overview-chart-series-item"
          :style="{ borderColor: getSeriesItemColor(i) }"
          @click="onSeriesItemClick(i)"
        >
          <div
            class="overview-chart-series-item__icon"
            :style="{ background: lightenColor(getSeriesItemColor(i)) }"
          >
            <i
              class="el-icon-s-data"
              :style="{ color: getSeriesItemColor(i) }"
            />
          </div>
          <div class="overview-chart-series-item__content">
            <div class="overview-chart-series-item__title">{{ sr.name }}</div>
            <div class="overview-chart-series-item__data">
              {{ roundAndFormatNumber(sr.overall) }}
            </div>
          </div>
        </div>
      </ElPopover>
    </div>
  </div>
</template>

<style lang="scss">
.overview-chart-series-selector {
  margin: 1rem 0;
  display: flex;
  gap: 1.2rem;
  flex-wrap: wrap;

  &__item {
    flex: 1 0 0;

    @include breakpoint(medium down) {
      flex-basis: 45% !important;
    }
  }

  &__popover {
    background-color: #20222a;
    color: #fff;
    padding: 0.5rem 0.7rem;
    border-radius: 8px;

    .popper__arrow::after {
      border-top-color: #20222a !important;
    }
  }
}

.overview-chart-series-item {
  border: 1px solid #eee;
  border-radius: 16px;
  background: #fff;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  flex: 1 0 0;
  align-items: center;
  gap: rem-calc(18px);
  transition: all ease 0.3s;

  @include breakpoint(medium down) {
    flex-basis: 45% !important;
  }

  &__icon {
    width: 50px;
    height: 50px;
    line-height: 50px;
    text-align: center;
    font-size: 1.2rem;
    flex-shrink: 0;
    background-color: #f1f2f9;
    color: #3e4151;
    border-radius: 50%;
    transition: all ease 0.3s;
  }

  &__title {
    color: #717485;
    font-weight: 500;
    font-size: 0.8rem;
    white-space: nowrap;
  }

  &__data {
    font-size: 1.2rem;
    color: #20222a;
    font-weight: 400;
  }
}
</style>
